/* eslint-disable @nx/enforce-module-boundaries */
import { type ConsentsState } from '@marriott/mi-legal-components';
/** Payload shape expected by create-account enrollment APIs (matches createAccountStore `consents`). */
export type EnrollmentConsentPayload = {
  type: string;
  optin: boolean;
  ctype: string;
  method?: string;
  isError?: boolean;
  /** True when the group is all checked or all unchecked (not mixed). */
  isAllCheckedOrUnchecked?: boolean;
};

export const CONSENT_SECTION_TYPE = {
  AGREE_ALL: 'agreeAll',
} as const;

export const CONSENT_TYPE = {
  PRIVACY_CONSENTS: 'privacyConsents',
  COMMUNICATION_PREFERENCES: 'communicationPreferences',
  USER_CONSENTS: 'userConsents',
};

export enum ConsentErrorType {
  NONE = 'none',
  NO_ENTRIES_FOUND = 'noEntriesFound',
  INTERNAL_VALIDATION_ERROR = 'internalValidationError',
  ALL_REQUIRED_CONSENTS_NOT_SELECTED = 'allRequiredConsentsNotSelected',
  ALL_ATOMIC_CONSENTS_NOT_SELECTED = 'allAtomicConsentsNotSelected',
  MULTIPLE_VAILDATION_FAILED = 'multipleValidationFailed',
}

export const LEGAL_CONSENTS_JOIN_NOW_OPTION = 'legal-consents:join-now';
export const LEGAL_CONSENTS_OCJ_OPTION = 'legal-consents:join-now-ocj';

function consentPayloadMergeKey(row: EnrollmentConsentPayload): string {
  const methodKey = row.method ?? '';
  return `${row.type}::${methodKey}::${row.ctype}`;
}

function checkboxToEnrollmentPayload(checkbox: {
  id?: string;
  category?: string | null;
  checked?: boolean;
}): EnrollmentConsentPayload | null {
  if (!checkbox?.category) {
    return null;
  }
  const i = checkbox.id?.indexOf('/') ?? -1;
  if (i !== -1) {
    const method = checkbox.id?.slice(0, i)?.trim();
    const type = checkbox.id?.slice(i + 1)?.trim();
    return {
      type: type || '',
      method: method || '',
      optin: !!checkbox.checked,
      ctype: checkbox.category || '',
    };
  }
  return {
    type: checkbox.id || '',
    optin: !!checkbox.checked,
    ctype: checkbox.category || '',
  };
}

export function updateConsentPayload(consentsState: { list?: ConsentsState['list'] }): EnrollmentConsentPayload[] {
  const byKey = new Map<string, EnrollmentConsentPayload>();
  const checkedByKey = new Map<string, boolean[]>();

  const mergeRow = (row: EnrollmentConsentPayload) => {
    const key = consentPayloadMergeKey(row);
    const existing = byKey.get(key);
    if (existing) {
      existing.optin = existing.optin && row.optin;
    } else {
      byKey.set(key, { ...row });
    }
  };

  for (const consentList of consentsState?.list ?? []) {
    const isAgreeAllSection = consentList?.sectionType === CONSENT_SECTION_TYPE.AGREE_ALL;
    for (const checkbox of consentList?.checkboxes ?? []) {
      if (isAgreeAllSection && checkbox.showCheckbox === true) {
        continue;
      }
      const row = checkboxToEnrollmentPayload(checkbox);
      if (!row) {
        continue;
      }
      const key = consentPayloadMergeKey(row);
      const bucket = checkedByKey.get(key) ?? [];
      bucket.push(!!checkbox.checked);
      checkedByKey.set(key, bucket);
      mergeRow(row);
    }
  }

  return Array.from(byKey.entries()).map(([key, row]) => {
    const checks = checkedByKey.get(key) ?? [];
    const isAllCheckedOrUnchecked = checks.length > 0 && (checks.every(c => c) || checks.every(c => !c));
    return { ...row, isAllCheckedOrUnchecked };
  });
}
